import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

type Props = {
  boardRef: React.RefObject<HTMLElement>;
  paginaRef: React.RefObject<HTMLElement>;
  selectableSelector?: string;
  onSelectionChange?: (ids: string[]) => void;
  onDeleteSelection?: (ids: string[]) => void;
  onSelect?: (ids: string[], pos?: { x: number; y: number } | null) => void;
};

export default function SelectionTool({
  boardRef,
  paginaRef,
  selectableSelector = '.selectable',
  onSelectionChange,
  onSelect
}: Props) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const [rect, setRect] = useState<null | { left: number; top: number; width: number; height: number }>(null);
  const selectedRef = useRef<Set<string>>(new Set());
  const draggingRef = useRef<{ active: boolean; originX: number; originY: number } | null>(null);

  useEffect(() => {
    if (paginaRef?.current) setPortalTarget(paginaRef.current);
  }, [paginaRef]);

  const getBoardRect = () => paginaRef.current?.getBoundingClientRect() ?? null;

  const rectsIntersect = (
    a: { left: number; top: number; width: number; height: number },
    b: { left: number; top: number; width: number; height: number }
  ) => !(a.left + a.width < b.left || b.left + b.width < a.left || a.top + a.height < b.top || b.top + b.height < a.top);

  // agora coletamos dentro da paginaRef
  const collectElementsInRect = (marquee: { left: number; top: number; width: number; height: number }) => {
    if (!paginaRef?.current) return [];
    const nodeList = Array.from(paginaRef.current.querySelectorAll(selectableSelector)) as HTMLElement[];
    const selected: string[] = [];
    const boardRect = getBoardRect();
    if (!boardRect) return [];

    nodeList.forEach(node => {
      const r = node.getBoundingClientRect();
      // coordenadas relativas ao board para comparação com marquee
      const rel = { left: r.left - boardRect.left, top: r.top - boardRect.top, width: r.width, height: r.height };
      if (rectsIntersect(rel, marquee)) {
        const id = node.getAttribute('data-id');
        if (id) selected.push(id);
      }
    });

    return selected;
  };

  const updateSelection = (ids: string[], additive = false, subtractive = false, clickPos?: { x: number; y: number } | null) => {
    if (!additive && !subtractive) selectedRef.current = new Set(ids);
    else if (additive) ids.forEach(id => selectedRef.current.add(id));
    else if (subtractive) ids.forEach(id => selectedRef.current.delete(id));

    const arr = Array.from(selectedRef.current.values());
    onSelectionChange?.(arr);
    onSelect?.(arr, clickPos ?? null);
  };

  const getMouseRelativeToBoard = (e: MouseEvent) => {
  if (!boardRef.current) return { x: 0, y: 0 };
  const boardRect = boardRef.current.getBoundingClientRect();
  return {
    x: e.clientX - boardRect.left,
    y: e.clientY - boardRect.top
  };
};


  useEffect(() => {
    const board = boardRef.current;
    if (!board) return;
  
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const boardRect = getBoardRect();
      if (!boardRect) return;
  
      // coordenadas RELATIVAS ao board — sem clamp

  
      const clickedSelectable = (e.target as HTMLElement).closest(selectableSelector);
      if (clickedSelectable) {
        const id = clickedSelectable.getAttribute('data-id');
        const alreadySelected = id && selectedRef.current.has(id);
        if (alreadySelected) {
          draggingRef.current = { active: true, originX: e.clientX, originY: e.clientY };
          document.body.style.userSelect = 'none';
          return;
        }
        startRef.current = null;
        return;
      }
  
      // iniciar marquee usando RELATIVOS exatamente como o mouse está
      const { x: relX, y: relY } = getMouseRelativeToBoard(e);
      startRef.current = { x: relX, y: relY };

// no mousedown
startRef.current = { x: e.pageX, y: e.pageY };
setRect({ left: e.pageX, top: e.pageY, width: 0, height: 0 });     document.body.style.userSelect = 'none';
    };
  
    const onMouseMove = (e: MouseEvent) => {
      if (!startRef.current) return;
      if (draggingRef.current?.active) {
        const dx = e.clientX - draggingRef.current.originX;
        const dy = e.clientY - draggingRef.current.originY;
        draggingRef.current.originX = e.clientX;
        draggingRef.current.originY = e.clientY;
        return;
      }
        if (!rect) return;
  
      const found = collectElementsInRect(rect);
      updateSelection(found, e.shiftKey, false);
      const cx = e.pageX;
const cy = e.pageY;
setRect({
  left: Math.min(startRef.current.x, cx),
  top: Math.min(startRef.current.y, cy),
  width: Math.abs(cx - startRef.current.x),
  height: Math.abs(cy - startRef.current.y)
});
setRect({
  left: Math.min(startRef.current.x, cx),
  top: Math.min(startRef.current.y, cy),
  width: Math.abs(cx - startRef.current.x),
  height: Math.abs(cy - startRef.current.y)
});
    };
  
    const onMouseUp = (e: MouseEvent) => {
      document.body.style.userSelect = '';
      if (draggingRef.current?.active) {
        draggingRef.current.active = false;
        draggingRef.current = null;
        return;
      }
  
      const boardRect = getBoardRect();
      const relX = boardRect ? e.clientX - boardRect.left : e.clientX;
      const relY = boardRect ? e.clientY - boardRect.top : e.clientY;
  
      if (!rect) {
        const clickedSelectable = (e.target as HTMLElement).closest(selectableSelector);
        if (clickedSelectable && draggingRef.current?.active) {
          const id = clickedSelectable.getAttribute('data-id') ?? '';
          updateSelection([id], e.shiftKey, false, { x: relX, y: relY });
        } else {
          //updateSelection([], false, false, { x: relX, y: relY });
        }
        startRef.current = null;
        return;
      }
  
      const found = collectElementsInRect(rect);
      updateSelection(found, e.shiftKey, false);
      startRef.current = null;
      setRect(null);
    };
  
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    board.addEventListener('mousedown', onMouseDown);
  
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      board.removeEventListener('mousedown', onMouseDown);
    };
  }, [boardRef, rect, selectableSelector, paginaRef]);

  const overlayContent = (
    <div
      ref={overlayRef}
      style={{position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 9999,    width: '100%',
    height: '100%', }}
    >
      {rect && (
        <div
          style={{
            position: 'absolute',
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            border: '2px solid #2b7cff',
            background: 'rgba(43,124,255,0.08)',
            pointerEvents: 'none',
          }}
        />
      )}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: rect ? 'auto' : 'none' }} />
    </div>
  );

  return portalTarget ? ReactDOM.createPortal(overlayContent, portalTarget) : overlayContent;
}
